import type { RouteObject } from 'react-router-dom';
import type { IRouteLayout } from '@components/ui/PageFrame/PageFrame';
import { ANNOTATIONS_SITE_ROUTE, DOCUMENTS_SITE_ROUTE, LOGISTICS_SITE_ROUTE } from './routePatterns';
import {
  PortfolioElement,
  MonitorElement,
  DashboardElement,
  DashboardRedirectElement,
  ActivitiesElement,
  ActivitiesRedirectElement,
  AnalyticsElement,
  AnalyticsRedirectElement,
  LegacyAnalyticsRedirectElement,
  ProjectOverviewElement,
  ContractorAnalysisElement,
  WorkersTimesheetElement,
  IncidentReportElement,
  DailyRouteElement,
  ObserveLocationElement,
  AiAgentElement,
  AiAgentRedirectElement,
  SchedulerElement,
  SchedulerRedirectElement,
  CadElement,
  CadRedirectElement,
  AnnotationsElement,
  DocumentsElement,
  DocumentsRedirectElement,
  LogisticsElement,
  LogisticsRedirectElement,
  AdminElement,
  AdminRedirectElement,
  ProjectMergeElement,
  MonitorRedirectElement,
  CamerasElement,
  CamerasRedirectElement,
  CommsElement,
  CommsRedirectElement,
  EquipmentElement,
  EquipmentRedirectElement,
  SubcontractorsElement,
  SubcontractorsRedirectElement,
  ProductivityElement,
  ProductivityRedirectElement,
  FlowViewElement,
  FinanceElement,
  SecurityElement,
  AlertSettingsElement,
  AlertSettingsRedirectElement,
  HelpCenterElement,
  GuideReaderElement,
} from './routes.elements';

/** A shell route and its frame: App.tsx renders every one inside `PageFrame` with this layout (a padded, untitled page when absent). */
export type IAppRoute = RouteObject & { layout?: IRouteLayout };

/** Each page's frame, declared once per route. Titles and subtitles are i18n keys; a page publishes anything that depends on its own state with the slots in `PageFrame`. */
const SCHEDULER_LAYOUT: IRouteLayout = { mode: 'fill', title: 'nav.item.scheduler.title', subtitle: 'scheduler.page.subtitle' };
/** A report is its own header (back button, title, stats) and runs edge to edge. */
const REPORT_LAYOUT: IRouteLayout = { mode: 'bleed' };

export const routes: IAppRoute[] = [
  { path: '/',                                                       element: PortfolioElement, layout: { title: 'topbar.portfolio', subtitle: 'portfolio.subtitle' } },

  // Overview — the per-site Dashboard / Command Center is unbuilt
  // (Phase 2); routes are reserved so the sidebar entry has a target.
  { path: '/dashboard/:siteId',                                      element: DashboardElement, layout: { title: 'nav.item.dashboard.title', subtitle: 'dashboard.header.subtitle' } },
  { path: '/dashboard',                                              element: DashboardRedirectElement },

  // Operations — the live RTLS Monitor is the page that used to live
  // at /dashboard/:siteId.
  { path: '/monitor/:siteId',                                        element: MonitorElement, layout: { mode: 'fill', title: 'nav.item.monitor.title', subtitle: 'monitor.header.subtitle' } },
  { path: '/monitor',                                                element: MonitorRedirectElement },
  { path: '/activities/:siteId',                                     element: ActivitiesElement, layout: { mode: 'fill', title: 'nav.item.activitiesIssues.title', subtitle: 'activities.page.subtitle' } },
  { path: '/activities',                                             element: ActivitiesRedirectElement },
  { path: '/cameras/:siteId',                                        element: CamerasElement, layout: { mode: 'inset', title: 'nav.item.cameras.title', subtitle: 'cameras.header.subtitle' } },
  { path: '/cameras',                                                element: CamerasRedirectElement },
  { path: '/comms/:siteId',                                          element: CommsElement, layout: { mode: 'inset', title: 'nav.item.commsHub.title', subtitle: 'comms.page.subtitle' } },
  { path: '/comms',                                                  element: CommsRedirectElement },

  // Planning
  { path: '/scheduler/:siteId/:scheduleNumber',                      element: SchedulerElement, layout: SCHEDULER_LAYOUT },
  { path: '/scheduler/:siteId',                                      element: SchedulerElement, layout: SCHEDULER_LAYOUT },
  { path: '/scheduler',                                              element: SchedulerRedirectElement },
  { path: LOGISTICS_SITE_ROUTE,                                      element: LogisticsElement, layout: { title: 'nav.item.logistics.title', subtitle: 'logistics.subtitle' } },
  { path: '/logistics',                                              element: LogisticsRedirectElement },
  { path: '/equipment/:siteId',                                      element: EquipmentElement, layout: { mode: 'inset', title: 'nav.item.equipment.title', subtitle: 'equipment.header.subtitle' } },
  { path: '/equipment',                                              element: EquipmentRedirectElement },

  // Engineering
  { path: '/cad/:siteId',                                            element: CadElement, layout: { mode: 'inset', title: 'nav.item.cad.title', subtitle: 'cad.shell.subtitle' } },
  { path: '/cad',                                                    element: CadRedirectElement },
  { path: DOCUMENTS_SITE_ROUTE,                                      element: DocumentsElement, layout: { mode: 'fill', title: 'nav.item.documents.title', subtitle: 'documents.page.subtitle' } },
  { path: ANNOTATIONS_SITE_ROUTE,                                    element: AnnotationsElement, layout: { mode: 'fill' } },
  { path: '/documents',                                              element: DocumentsRedirectElement },

  // Performance. Analytics used to be nested under /monitor; the legacy
  // route forwards old bookmarks and shared report links.
  { path: '/analytics/:siteId',                                      element: AnalyticsElement, layout: { title: 'nav.item.analytics.title', subtitle: 'analytics.subtitle' } },
  { path: '/analytics/:siteId/projectOverview/:reportId',            element: ProjectOverviewElement, layout: REPORT_LAYOUT },
  { path: '/analytics/:siteId/contractorAnalysis/:reportId',         element: ContractorAnalysisElement, layout: REPORT_LAYOUT },
  { path: '/analytics/:siteId/workersTimesheet/:reportId',           element: WorkersTimesheetElement, layout: REPORT_LAYOUT },
  { path: '/analytics/:siteId/incidentReport/:reportId',             element: IncidentReportElement, layout: REPORT_LAYOUT },
  { path: '/analytics/:siteId/dailyRoute/:reportId',                 element: DailyRouteElement, layout: REPORT_LAYOUT },
  { path: '/analytics/:siteId/observeLocation/:reportId',            element: ObserveLocationElement, layout: REPORT_LAYOUT },
  { path: '/analytics',                                              element: AnalyticsRedirectElement },
  { path: '/monitor/:siteId/analytics/*',                            element: LegacyAnalyticsRedirectElement },
  { path: '/subcontractors/:siteId',                                 element: SubcontractorsElement, layout: { title: 'nav.item.subcontractors.title', subtitle: 'subcontractors.subtitle' } },
  { path: '/subcontractors',                                         element: SubcontractorsRedirectElement },
  { path: '/productivity/:siteId',                                   element: ProductivityElement, layout: { title: 'nav.item.productivity.title', subtitle: 'productivity.header.subtitle' } },
  { path: '/productivity',                                           element: ProductivityRedirectElement },

  // Platform
  { path: '/flow-view',                                              element: FlowViewElement, layout: { title: 'nav.item.flowView.title', subtitle: 'flowView.subtitle' } },
  { path: '/ai-agent/:siteId',                                       element: AiAgentElement, layout: { mode: 'fill', title: 'nav.item.aiAgent.title', subtitle: 'aiAgent.subtitle' } },
  { path: '/ai-agent',                                               element: AiAgentRedirectElement },

  // Business
  { path: '/finance',                                                element: FinanceElement, layout: { title: 'nav.item.finance.title', subtitle: 'finance.subtitle' } },

  // Account settings — entered from the user menu. Security is per account;
  // alert settings are per project, so they name the site.
  { path: '/settings/security',                                      element: SecurityElement, layout: { title: 'auth.mfa.settings.pageTitle', subtitle: 'auth.mfa.settings.pageSubtitle' } },
  { path: '/settings/alerts/:siteId',                                element: AlertSettingsElement, layout: { title: 'alertSettings.personal.title', subtitle: 'alertSettings.personal.intro' } },
  { path: '/settings/alerts',                                        element: AlertSettingsRedirectElement },

  // Help Center — global, not site-scoped (spec: 2026-10-06-help-center-design).
  { path: '/help',                                                   element: HelpCenterElement, layout: { title: 'nav.item.pageGuides.title', subtitle: 'help.lede' } },
  { path: '/help/:slug',                                             element: GuideReaderElement, layout: { mode: 'bleed' } },

  // Admin — the landing page is currently the live Tags Inventory (site-scoped).
  { path: '/admin/project-merge',                                    element: ProjectMergeElement, layout: { title: 'nav.item.projectMerge.title', subtitle: 'projectMerge.subtitle' } },
  { path: '/admin/:siteId',                                          element: AdminElement, layout: { mode: 'fill' } },
  { path: '/admin',                                                  element: AdminRedirectElement },
];
